import axios from 'axios'
import qs from 'qs'
import moment from 'moment'
import jwtDecode from 'jwt-decode'
import * as constants from '@/mixins/constants'
import { HTTP_REQUEST } from '@/mixins/constants'
import { getCookie, setCookie } from '@/plugins/util'
import VueCookie from 'vue-cookie'

export const API = () => {
  const instance = axios.create({
    baseURL: process.env.VUE_APP_API_URL,
    timeout: 20000
  })

  return instance
}

export const instance = axios.create({
  baseURL: process.env.VUE_APP_API_URL,
  timeout: 30000
})

export function getToken() {
  const token = (window && window.z_web_in_app_token) || getCookie('token') || ''
  return token
}

export function getRefreshToken() {
  return getCookie(constants.COOKIE_NAME__REFRESH_TOKEN) || ''
}

// OW-1567: shared single-flight refresh. Nhiều request đồng thời gặp 401 → chỉ 1 lần hit
// /token/refresh, tất cả request khác đợi rồi retry với token mới.
let _refreshPromise = null

function persistNewTokens({ token, refresh_token }) {
  const exp = constants.COOKIE_EXPIRES_MAX_AGE__3M
  if (token) setCookie(constants.COOKIE_NAME__TOKEN, token, exp)
  if (refresh_token) setCookie(constants.COOKIE_NAME__REFRESH_TOKEN, refresh_token, exp)
  // Đồng bộ token cho native app WebView (nếu có).
  if (window && token) window.z_web_in_app_token = token
  // Bắn event cho phần khác của app (parent embed iframe, store, …) reset lại nếu cần.
  if (window && token) {
    window.dispatchEvent(new CustomEvent('onwheel:token-refreshed', { detail: { token } }))
  }
}

function clearAuthCookies() {
  VueCookie.delete(constants.COOKIE_NAME__TOKEN)
  VueCookie.delete(constants.COOKIE_NAME__REFRESH_TOKEN)
  if (window) delete window.z_web_in_app_token
}

async function refreshTokens() {
  if (_refreshPromise) return _refreshPromise
  const currentToken = getToken()
  const refreshToken = getRefreshToken()
  if (!currentToken || !refreshToken) throw new Error('MISSING_REFRESH_INPUT')

  _refreshPromise = (async () => {
    try {
      const decoded = jwtDecode(currentToken)
      const res = await axios.post(
        `${process.env.VUE_APP_API_URL}${constants.API.user.auth.refreshToken}`,
        {
          mobile: decoded.cid,
          refresh_token: refreshToken,
          type: decoded.type || 'web',
          imei: decoded.imei
        },
        { timeout: HTTP_REQUEST.TIMEOUT, headers: { 'Content-Type': 'application/json' } }
      )
      if (!res.data || !res.data.token) throw new Error('REFRESH_EMPTY_RESPONSE')
      persistNewTokens(res.data)
      return res.data.token
    } finally {
      _refreshPromise = null
    }
  })()

  return _refreshPromise
}

instance.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config || {}
    const status = error.response && error.response.status
    const code = error.response && error.response.data && error.response.data.code
    const isUnauthorized = status === 401 || code === 'NOT_AUTHORIZED' || code === 'TOKEN_EXPIRED'
    if (!isUnauthorized || original._retry || !getRefreshToken()) {
      return Promise.reject(error)
    }
    original._retry = true
    try {
      const newToken = await refreshTokens()
      // Replace token ở cả header lẫn body/query để retry.
      if (original.headers) original.headers.Authorization = `Bearer ${newToken}`
      if (typeof original.data === 'string' && original.data.includes('token=')) {
        original.data = original.data.replace(
          /token=[^&]*/,
          `token=${encodeURIComponent(newToken)}`
        )
      }
      if (original.url && original.url.includes('token=')) {
        original.url = original.url.replace(/token=[^&]*/, `token=${encodeURIComponent(newToken)}`)
      }
      return instance(original)
    } catch (refreshErr) {
      clearAuthCookies()
      return Promise.reject(error)
    }
  }
)

function addAuthorizationHeader(config = {}, token) {
  let _config = { ...config }

  if (_config.token_required) {
    _config.headers = _config.headers || {}

    _config.headers.Authorization = `Bearer ${token}`
  }

  return _config
}

/*
 * Wraps axios and provides
 * more convenient POST method
 * calls with data
 */
export function post(uri, data, requestConfig = {}) {
  let defaultConfig = {
    token_required: true,
    stringify_data: true,
    timeout: HTTP_REQUEST.TIMEOUT
  }
  const { token_required_header, ...restConfig } = requestConfig
  let config = { ...defaultConfig, ...restConfig }

  if (config.token_required) {
    const token = getToken() || data.token
    config = addAuthorizationHeader(config, token)
    if (!token_required_header) data = { ...data, token }
  }

  if (config.stringify_data && data) {
    data = qs.stringify(data)
  }
  return instance.post(uri, data, config)
}

// OW-1567: detect 401 từ response worker (khác axios error).
function isWorkerUnauthorized(response) {
  if (!response) return false
  const status = response.status
  const code = response.data && response.data.code
  return status === 401 || code === 'NOT_AUTHORIZED' || code === 'TOKEN_EXPIRED'
}

export async function postViaWebWorker(uri, data = {}, requestConfig = {}, _retry = false) {
  let defaultConfig = {
    token_required: true,
    timeout: HTTP_REQUEST.TIMEOUT
  }
  const { token_required_header, ...restConfig } = requestConfig
  let config = { ...defaultConfig, ...restConfig }

  if (config.token_required) {
    const token = getToken() || data.token
    config = addAuthorizationHeader(config, token)

    if (!token_required_header) data = { ...data, token }
  }

  let wwConfig
  if (Object.keys(data).length > 0) {
    wwConfig = {
      method: 'POST',
      data,
      ...config
    }
  }

  const baseUrl = config.baseURL || process.env.VUE_APP_API_URL
  const params = {
    url: `${baseUrl}${uri}`,
    ...wwConfig
  }

  const response = await window.$mainWorker.callAPI(params)
  if (isWorkerUnauthorized(response) && !_retry && getRefreshToken()) {
    try {
      await refreshTokens()
      return postViaWebWorker(uri, data, requestConfig, true)
    } catch (e) {
      clearAuthCookies()
    }
  }
  return new Promise((resolve, reject) => {
    const { status, data } = response
    if (status === 200) {
      resolve(data)
    } else {
      reject(response)
    }
  })
}

/*
 * Wraps axios and provides
 * more convenient GET method
 * calls with data.
 */
export function get(uri, data = {}, requestConfig = {}) {
  let defaultConfig = {
    token_required: true,
    stringify_data: true
  }
  const { token_required_header, ...restConfig } = requestConfig
  let config = { ...defaultConfig, ...restConfig }

  if (config.token_required) {
    const token = getToken() || data.token
    config = addAuthorizationHeader(config, token)

    if (!token_required_header) data = { ...data, token }
  }

  if (Object.keys(data).length > 0) {
    let encodedData
    if (config.stringify_data) {
      encodedData = qs.stringify(data, {
        encodeValuesOnly: true
      })
    } else {
      encodedData = qs.stringify(data, { encode: false })
    }

    if (encodedData) {
      uri = `${uri}?${encodedData}`
    }
  }
  return instance.get(uri, config)
}

export async function getViaWebWorker(uri, data = {}, requestConfig = {}, _retry = false) {
  const originalUri = uri
  const originalData = data
  let defaultConfig = {
    token_required: true,
    stringify_data: true,
    array_format: ''
  }

  const { token_required_header, ...restConfig } = requestConfig
  let config = { ...defaultConfig, ...restConfig }

  if (config.token_required) {
    const token = getToken() || data.token
    config = addAuthorizationHeader(config, token)
    if (!token_required_header) data = { ...data, token }
  }

  if (data && Object.keys(data).length > 0) {
    let encodedData
    if (config.stringify_data) {
      encodedData = qs.stringify(data, { encodeValuesOnly: true, arrayFormat: config.array_format })
    } else {
      encodedData = qs.stringify(data, { encode: false })
    }

    uri = `${uri}?${encodedData}`
  }

  const baseUrl = config.baseURL || process.env.VUE_APP_API_URL
  const params = {
    url: `${baseUrl}${uri}`,
    method: 'GET',
    ...config
  }
  const response = await window.$mainWorker.callAPI(params)
  if (isWorkerUnauthorized(response) && !_retry && getRefreshToken()) {
    try {
      await refreshTokens()
      return getViaWebWorker(originalUri, originalData, requestConfig, true)
    } catch (e) {
      clearAuthCookies()
    }
  }
  return new Promise((resolve, reject) => {
    const { status, data } = response || {}
    if (status === 200) {
      resolve(data)
    } else {
      reject(response)
    }
  })
}

/*
 * Wraps axios and provides
 * more convenient PUT method
 * calls with data.
 */

export function put(uri, data, requestConfig = {}) {
  let defaultConfig = {
    token_required: true,
    stringify_data: true,
    timeout: HTTP_REQUEST.TIMEOUT
  }

  const { token_required_header, header_json, ...restConfig } = requestConfig
  let config = { ...defaultConfig, ...restConfig }

  if (config.token_required) {
    const token = getToken() || data.token
    config = addAuthorizationHeader(config, token)
    if (!token_required_header) data = { ...data, token }
  }

  if (header_json) {
    config.headers['Content-Type'] = 'application/json'
  } else {
    if (config.stringify_data && data) {
      data = qs.stringify(data)
    }
  }

  return instance.put(uri, data, config)
}

export function patch(uri, data, requestConfig = {}) {
  let defaultConfig = {
    token_required: true,
    timeout: HTTP_REQUEST.TIMEOUT
  }

  const { token_required_header, ...restConfig } = requestConfig
  let config = { ...defaultConfig, ...restConfig }

  if (config.token_required) {
    const token = getToken() || data.token
    config = addAuthorizationHeader(config, token)
    if (!token_required_header) data = { ...data, token }
  }

  return instance.patch(uri, data, config)
}

/*
 * Wraps axios and provides
 * more convenient DELETE method
 * calls with data.
 */

export function remove(uri, data = {}, requestConfig = {}) {
  let defaultConfig = {
    token_required: true,
    stringify_data: true,
    timeout: HTTP_REQUEST.TIMEOUT
  }
  const { token_required_header, ...restConfig } = requestConfig
  let config = { ...defaultConfig, ...restConfig }

  if (config.token_required) {
    const token = getToken() || data.token
    config = addAuthorizationHeader(config, token)
    if (!token_required_header) data = { ...data, token }
  }

  if (Object.keys(data).length > 0) {
    let encodedData
    if (config.stringify_data) {
      encodedData = qs.stringify(data, {
        encodeValuesOnly: true
      })
    } else {
      encodedData = qs.stringify(data, { encode: false })
    }

    uri = `${uri}?${encodedData}`
  }

  return instance.delete(uri, config)
}

export function removeHaveData(uri, data = {}, requestConfig = {}) {
  let defaultConfig = {
    token_required: true,
    stringify_data: true,
    timeout: HTTP_REQUEST.TIMEOUT
  }
  const { token_required_header, ...restConfig } = requestConfig
  let config = { ...defaultConfig, ...restConfig }

  if (config.token_required) {
    const token = getToken() || data.token
    config = addAuthorizationHeader(config, token)
    if (!token_required_header) data = { ...data, token }
  }

  if (Object.keys(data).length > 0) {
    config.data = data
  }

  return instance.delete(uri, config)
}

export function getOrderFromBigC(uri, data = {}, config = {}) {
  let current = moment()
  let reqTime = `${current.format('DDMMYYYY')}_${current.format('HH:mm:ss:SSS')}`
  const baseUrl = `${process.env.VUE_APP_BIGC_API_URL}${uri}`

  let headers = {
    'Content-Type': 'application/json',
    partnerCode: process.env.VUE_APP_BIGC_API_PARTNER_CODE,
    client_id: process.env.VUE_APP_BIGC_API_CLIENT_ID,
    client_secret: process.env.VUE_APP_BIGC_API_CLIENT_SECRET,
    requestTime: reqTime,
    transactionChannel: config.channel
  }

  return instance.post(baseUrl, data, { headers: headers })
}

export function processOrderFromOnWheel(uri, data = {}) {
  const baseUrl = `${process.env.VUE_APP_ONWHEEL_UTILS_URL}${uri}`

  let headers = {
    'Content-Type': 'application/json',
    'partner-code': 'centralretail',
    'client-id': process.env.VUE_APP_BIGC_API_CLIENT_ID,
    'client-secret': process.env.VUE_APP_BIGC_API_CLIENT_SECRET
  }

  return instance.post(baseUrl, data, { headers: headers, timeout: 180000 })
}

export function postDataOnWheel(uri, data = {}) {
  const baseUrl = `${process.env.VUE_APP_ONWHEEL_ADMIN_URL}${uri}`
  const payload = {
    ...data,
    token: getToken()
  }

  const headers = {
    'Content-Type': 'application/json',
    Authorization: `Bearer ${payload.token}`
  }

  return instance.post(baseUrl, payload, {
    headers: headers,
    timeout: 180000
  })
}

export function getOSRMDistance(uri, data = {}) {
  const baseUrl = `${process.env.VUE_APP_ONWHEEL_UTILS_URL}${uri}`
  const headers = { 'Content-Type': 'application/json' }
  return instance.post(baseUrl, data, { headers: headers })
  // const response = instance.post(baseUrl, data, { headers: headers })
  // return new Promise((resolve, reject) => {
  //   const { status, data } = response || {}
  //   if (status === 200) {
  //     resolve(data)
  //   } else {
  //     reject(response)
  //   }
  // })
}
